import * as React from 'react';
import Link from 'next/link';
import { FormItem } from '@/components/dashboard/settings';
import { Template } from './Template';
import { useNotification } from '@/hooks';
import { FieldValues, useForm } from 'react-hook-form';
import { Toggle, Typography } from '@/components/common';
import { NotificationsPayload, UserI } from '@/interfaces';
import { userLawyerService } from '@/api';
import AppRoutes from '@/const/routes';
import { InfoCircle } from '@/public/icons/tsx';
import { formatErrorMessage } from '@/lib';

interface NotificationPreferencesProps {
	userInformation?: UserI;
	refetch: () => void;
}

export const NotificationPreferences: React.FC<
	NotificationPreferencesProps
> = ({ refetch, userInformation }) => {
	const { toastSuccess, toastError } = useNotification();
	const [disabled, setDisabled] = React.useState<boolean>(false);

	const { register, setValue, handleSubmit, reset, watch, resetField } =
		useForm({
			mode: 'onChange',
		});
	const [initialValues, setInitialValues] = React.useState({
		receive_platform: true,
		receive_sms: false,
		notification_new_messages: true,
		notification_new_cases: true,
		receive_daily_notifications_digest_email: true,
	});

	React.useEffect(() => {
		if (userInformation) {
			const initialData = {
				receive_platform: userInformation.notification_by_platform,
				receive_sms: userInformation.notification_by_sms,
				notification_new_messages:
					userInformation.notification_about_new_messages,
				notification_new_cases: userInformation.notification_about_new_cases,
				receive_daily_notifications_digest_email:
					userInformation.receive_daily_notifications_digest_email,
			};
			setInitialValues(initialData);
			reset(initialData); // Establece los valores iniciales en el formulario
		}
	}, [userInformation]);

	const handleSave = async (data: FieldValues): Promise<void> => {
		try {
			const newData: NotificationsPayload = {
				notificationAboutNewCases: data.notification_new_cases,
				notificationAboutNewMessages: data.notification_new_messages,
				notificationByPlatform: data.receive_platform,
				notificationBySms: data.receive_sms,
				receiveDailyNotificationsDigestEmail:
					data.receive_daily_notifications_digest_email,
			};
			if (userInformation?.role === 'lawyer') {
				await userLawyerService.updateNotificationsPreferences(newData);
			}
			refetch();
			toastSuccess('Your changes have been successfully saved.');
			setDisabled(false);
		} catch (error) {
			toastError(formatErrorMessage(undefined));
		}
	};

	const handleCancel = (): void => {
		reset(initialValues);
		setDisabled(false);
	};

	const isDisabledButton = (): void => {
		if (userInformation && !userInformation.is_phone_number_verified) {
			setDisabled(true);
			resetField('receive_sms');
		} else {
			setDisabled(false);
		}
	};

	return (
		<Template
			title="Customize your notifications preferences"
			onClickSave={handleSubmit(handleSave)}
			onClickCancel={handleCancel}
		>
			<FormItem
				label="Preferred notification method"
				className="!items-start py-6"
			>
				<div>
					<div className="flex items-center gap-4">
						{userInformation && (
							<div onClick={isDisabledButton}>
								<Toggle
									name="receive_sms"
									isActive={watch('receive_sms')}
									register={register}
									setValue={setValue}
									isDisabled={disabled}
								/>
							</div>
						)}
						<Typography type="regular-14" className="text-black">
							Receive by SMS
						</Typography>
					</div>
					{disabled && (
						<div className="flex items-center gap-x-1.5 mt-[18.5px]">
							<InfoCircle fill="#FF0043" width="16" height="16" />
							<Typography type="regular-14" className="text-main-red">
								You need to{' '}
								<Link
									className="font-semibold text-primary"
									href={AppRoutes.CLIENTAPP_SETTINGS_PERSONAL}
								>
									Verify your phone
								</Link>{' '}
								first.
							</Typography>
						</div>
					)}
				</div>
				<div className="flex items-center gap-4  mt-5">
					{userInformation && (
						<Toggle
							name="receive_platform"
							isActive={watch('receive_platform')}
							register={register}
							setValue={setValue}
							isDisabled={userInformation.role === 'lawyer'}
						/>
					)}
					<Typography type="regular-14" className="text-black">
						Receive via platform
					</Typography>
				</div>
				<div className="flex items-center gap-4  mt-5">
					{userInformation && (
						<Toggle
							name="receive_email"
							isActive
							register={register}
							setValue={setValue}
							isDisabled
						/>
					)}
					<Typography type="regular-14" className="text-black">
						Receive via Email
					</Typography>
				</div>
			</FormItem>
			<FormItem
				label="Select notifications to receive"
				className="!py-8 !items-start"
			>
				<div className="flex items-center gap-4">
					{userInformation && (
						<Toggle
							name="notification_new_messages"
							isActive={watch('notification_new_messages')}
							register={register}
							setValue={setValue}
						/>
					)}
					<Typography type="regular-14" className="text-black">
						Email notifications for new messages in your Inbox
					</Typography>
				</div>
				<div className="flex items-center gap-4 mt-5">
					{userInformation && (
						<Toggle
							name="receive_daily_notifications_digest_email"
							isActive={watch('receive_daily_notifications_digest_email')}
							register={register}
							setValue={setValue}
						/>
					)}
					<Typography type="regular-14" className="text-black">
						Daily Notifications Digest
					</Typography>
				</div>
				{userInformation?.role === 'client' && (
					<div className="flex items-center gap-4 mt-5">
						{userInformation && (
							<Toggle
								name="notification_new_cases"
								isActive={watch('notification_new_cases')}
								register={register}
								setValue={setValue}
							/>
						)}
						<Typography type="regular-14" className="text-black">
							Immediate alerts for new cases that match your profile and
							opportunities to apply
						</Typography>
					</div>
				)}
			</FormItem>
		</Template>
	);
};
